import React from "react";
import type { TaktEditSummary, TaktEditSummaryActivity } from "../../../../models";
import CodePill from "../../../shared/CodePill/CodePill";
import { naturalCompare } from "../../../../utils/sort";
import "./ConfirmCommitModal.css";

interface Props {
  summary: TaktEditSummary;
  committing: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const acts = (n: number) => `${n} ${n === 1 ? "activity" : "activities"}`;

/** A colored code pill, or a muted "no code" placeholder. */
const codeChip = (code: string, color: string | null) =>
  code ? (
    <CodePill code={code} color={color} className="tecc-pill" />
  ) : (
    <span className="tecc-nocode">no code</span>
  );

/** Group a section's edited activities by the zone/level they end up in, so the
 *  modal reads "section → zone/level → the activities that changed there". */
function groupByZone(
  list: TaktEditSummaryActivity[],
): { zone: string; activities: TaktEditSummaryActivity[] }[] {
  const m = new Map<string, TaktEditSummaryActivity[]>();
  for (const a of list) {
    const z = a.to_zone || "Untagged";
    const arr = m.get(z);
    if (arr) arr.push(a);
    else m.set(z, [a]);
  }
  return Array.from(m.entries())
    .sort((a, b) => naturalCompare(a[0], b[0]))
    .map(([zone, activities]) => ({ zone, activities }));
}

/** Confirmation modal shown before a Takt Editor save: structure changes grouped
 *  by section → zone/level on the left, work-code changes on the right. Only the
 *  dimensions that actually changed are shown (no "X → X"). */
/** Trade name with its color swatch, or a muted "Unassigned". */
const tradeChip = (name: string | null, color: string | null) =>
  name ? (
    <span className="tecc-trade">
      <span
        className="tecc-trade-swatch"
        style={{ backgroundColor: color || "#666" }}
      />
      {name}
    </span>
  ) : (
    <span className="tecc-trade tecc-trade--none">Unassigned</span>
  );

const ConfirmCommitModal: React.FC<Props> = ({
  summary,
  committing,
  error,
  onConfirm,
  onCancel,
}) => {
  const {
    sections,
    work_codes,
    trades,
    trade_crud,
    unplaced,
    section_order_changed,
    totals,
  } = summary;
  const onBackdrop = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!committing) onCancel();
  };
  return (
    <div className="tecc-overlay" onClick={onBackdrop}>
      <div
        className="tecc-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Review changes"
      >
        <div className="tecc-head">
          <h2 className="tecc-title">Review changes</h2>
          <span className="tecc-sub">
            {totals.sections} section{totals.sections === 1 ? "" : "s"} ·{" "}
            {totals.work_codes} work code{totals.work_codes === 1 ? "" : "s"} ·{" "}
            {acts(totals.activities)}
            {totals.trades > 0 &&
              ` · ${totals.trades} trade change${totals.trades === 1 ? "" : "s"}`}
          </span>
        </div>

        {unplaced.length > 0 && (
          <div className="tecc-warn" role="alert">
            <span className="tecc-warn-icon" aria-hidden>
              ⚠
            </span>
            <div className="tecc-warn-body">
              <strong>
                {acts(unplaced.length)} won&rsquo;t appear on the board
              </strong>
              <span className="tecc-warn-sub">
                Coded but missing a zone — assign a Zone so they show on the Takt
                board.
              </span>
              <ul className="tecc-warn-list">
                {unplaced.slice(0, 6).map((u, i) => (
                  <li key={i}>
                    <span className="tecc-warn-name" title={u.name}>
                      {u.name}
                    </span>
                    {u.section && (
                      <span className="tecc-warn-section">{u.section}</span>
                    )}
                  </li>
                ))}
                {unplaced.length > 6 && (
                  <li className="tecc-warn-more">
                    +{unplaced.length - 6} more
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}

        <div className="tecc-body">
          <div className="tecc-col">
            <div className="tecc-col-head">Sections &amp; structure</div>
            {section_order_changed && (
              <div className="tecc-note">Section order changed</div>
            )}
            {sections.length === 0 && !section_order_changed && (
              <div className="tecc-empty">No section changes</div>
            )}
            {sections.map((s) => (
              <div key={s.name} className="tecc-card">
                <div className="tecc-card-title">
                  {s.new_name ? (
                    <>
                      <span className="tecc-old">{s.name || "— no section —"}</span>
                      <span className="tecc-arrow">→</span>
                      <strong>{s.new_name}</strong>
                    </>
                  ) : (
                    s.name || "— no section —"
                  )}
                </div>

                {(s.zone_renames.length > 0 ||
                  s.level_renames.length > 0 ||
                  s.reordered) && (
                  <ul className="tecc-changes">
                    {s.zone_renames.map((z, i) => (
                      <li key={`z${i}`}>
                        Zone <span className="tecc-old">{z.old}</span>
                        <span className="tecc-arrow">→</span>
                        <strong>{z.new}</strong>
                      </li>
                    ))}
                    {s.level_renames.map((l, i) => (
                      <li key={`l${i}`}>
                        Level {l.zone} / <span className="tecc-old">{l.old}</span>
                        <span className="tecc-arrow">→</span>
                        <strong>{l.new}</strong>
                      </li>
                    ))}
                    {s.reordered && <li>Zones / levels reordered</li>}
                  </ul>
                )}

                {groupByZone(s.activities).map((grp) => (
                  <div key={grp.zone} className="tecc-zonegroup">
                    <div className="tecc-zone">{grp.zone}</div>
                    {grp.activities.map((a, i) => {
                      const movedZone = a.from_zone && a.from_zone !== a.to_zone;
                      const movedSection =
                        a.from_section && a.from_section !== a.to_section;
                      return (
                        <div key={i} className="tecc-act">
                          <span className="tecc-act-name" title={a.name}>
                            {a.name}
                          </span>
                          {a.from_code !== a.to_code && (
                            <span className="tecc-act-delta">
                              {codeChip(a.from_code, a.from_color)}
                              <span className="tecc-arrow">→</span>
                              {codeChip(a.to_code, a.to_color)}
                            </span>
                          )}
                          {(movedZone || movedSection) && (
                            <span className="tecc-act-moved">
                              from {movedSection ? `${a.from_section} · ` : ""}
                              {a.from_zone || "Untagged"}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="tecc-col tecc-col--right">
            <div className="tecc-col-head">Work codes</div>
            {work_codes.length === 0 && (
              <div className="tecc-empty">No work-code changes</div>
            )}
            {work_codes.map((w, i) => (
              <div key={i} className="tecc-card">
                <div className="tecc-card-title">
                  <CodePill
                    code={w.new_code || w.code}
                    color={w.color}
                    className="tecc-pill"
                  />
                  {(w.new_name || w.prefix) && (
                    <span className="tecc-prefix">{w.new_name || w.prefix}</span>
                  )}
                  {w.is_new && <span className="tecc-new">NEW</span>}
                  {w.merge && <span className="tecc-merge">merge</span>}
                </div>
                <ul className="tecc-changes">
                  {w.new_code && w.new_code !== w.code && (
                    <li>
                      Code <span className="tecc-old">{w.code}</span>
                      <span className="tecc-arrow">→</span>
                      <strong>{w.new_code}</strong>
                    </li>
                  )}
                  {w.new_name && (
                    <li>
                      Renamed<span className="tecc-arrow">→</span>
                      <strong>{w.new_name}</strong>
                    </li>
                  )}
                  {w.new_color && !w.is_new && <li>Recolored</li>}
                  <li className="tecc-affected">{acts(w.affected_count)}</li>
                </ul>
              </div>
            ))}

            {/* Trade changes — only rendered when the save touches trades. */}
            {(trades.length > 0 || trade_crud.length > 0) && (
              <>
                <div className="tecc-col-head tecc-col-head--trades">Trades</div>
                {trade_crud.map((c, i) => (
                  <div key={`tc${i}`} className="tecc-card">
                    <div className="tecc-card-title">
                      {tradeChip(c.name, c.fill_color)}
                      {c.action === "create" && <span className="tecc-new">NEW</span>}
                      {c.action === "delete" && (
                        <span className="tecc-removed">removed</span>
                      )}
                    </div>
                    {c.action === "update" && (
                      <ul className="tecc-changes">
                        {c.new_name && (
                          <li>
                            Renamed<span className="tecc-arrow">→</span>
                            <strong>{c.new_name}</strong>
                          </li>
                        )}
                        {c.recolored && <li>Recolored</li>}
                      </ul>
                    )}
                    {c.action === "delete" && c.affected_count > 0 && (
                      <ul className="tecc-changes">
                        <li className="tecc-affected">
                          {acts(c.affected_count)} become unassigned
                        </li>
                      </ul>
                    )}
                  </div>
                ))}
                {trades.map((t, i) => (
                  <div key={`t${i}`} className="tecc-card">
                    <div className="tecc-card-title">
                      {codeChip(t.code, t.color)}
                      {t.prefix && <span className="tecc-prefix">{t.prefix}</span>}
                      <span className="tecc-trade-section">
                        {t.section || "— no section —"}
                      </span>
                    </div>
                    <ul className="tecc-changes">
                      <li>
                        {tradeChip(t.from_trade, t.from_trade_color)}
                        <span className="tecc-arrow">→</span>
                        {tradeChip(t.to_trade, t.to_trade_color)}
                      </li>
                      <li className="tecc-affected">{acts(t.affected_count)}</li>
                    </ul>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {error && <div className="tecc-error">{error}</div>}

        <div className="tecc-foot">
          <button
            type="button"
            className="tecc-cancel"
            onClick={onCancel}
            disabled={committing}
          >
            Cancel
          </button>
          <button
            type="button"
            className="tecc-confirm"
            onClick={onConfirm}
            disabled={committing}
          >
            {committing ? "Saving…" : "Confirm & Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmCommitModal;
